import type { Fn } from '@scansource-brazil/zeus-core-ui/types';
import type { IOrder, IOrderItems, IOrderList } from '~/models/Orders';

import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { isNotEmptyValue } from '@scansource-brazil/zeus-core-ui/data';
import { useLayoutStore } from '~/stores/useLayoutStore';
import { useAuthStore } from '~/stores/useAuthStore';
import usePaginator from '~/composables/usePaginator';
import { listOrders, showLastOrder } from '~/services/apiOrders';
import { isNullOrEmptyObject } from '~/utils/dataUtils';
import { ORDER_LIST_FILTERS } from '~/helpers/Orders.const';
import { PAGINATION } from '~/helpers/constants';

export const useOrdersStore = defineStore(
  'MY_SCAN_SOURCE_ORDERS_STORE',
  () => {
    const LAYOUT_STORE = useLayoutStore();
    const AUTH_STORE = useAuthStore();

    const { isMobile } = storeToRefs(LAYOUT_STORE);
    const { session } = storeToRefs(AUTH_STORE);

    const orderListPaginator = usePaginator();
    const {
      pagination: orderListPagination,
      CallPaginate: CallOrderListPaginate,
      CallSetPagination,
    } = orderListPaginator;

    const loadingOrders = ref<boolean>(false);
    const orderListFilters = ref({ ...ORDER_LIST_FILTERS });
    const orderList = ref<IOrderList>([]);
    const order = ref<IOrder>();
    const lastOrder = ref<IOrder | null>(null);
    const orderItems = ref<IOrderItems>({} as IOrderItems);
    const orderStatements = ref([]);
    const noDataFiltered = ref(false);
    const expiration = ref<Date | null>(null);

    const hasOrderItems = computed(() => !isNullOrEmptyObject(orderItems.value));

    const hasInvoicedItems = computed(
      () => hasOrderItems.value && orderItems.value.invoicedItems.length >= 1,
    );

    const hasUnbilledItems = computed(
      () => hasOrderItems.value && orderItems.value.unbilledItems.length >= 1,
    );

    const orderItemsInvoiced = computed(
      () => hasOrderItems.value && orderItems.value.invoicedItems,
    );

    const hasFilteredData = computed(() =>
      noDataFiltered.value ? 'TOAST.DETAILS.NO_DATA_FILTERED' : '',
    );

    const isNotOneTimePayment = computed(() => {
      if (!order.value) return true;
      return order.value.paymentMethod !== 'A VISTA';
    });

    const isExpired = computed(() => {
      const validator = new Date().getTime();
      if (!expiration.value) return true;

      return expiration.value ? new Date(expiration.value).getTime() < validator : true;
    });

    function setLastOrder(_order: IOrder | null) {
      _order ? (lastOrder.value = _order) : (lastOrder.value = null);
    }

    function setOrder(_order: IOrder) {
      order.value = _order;
    }
    function setOrderItems(items: IOrderItems) {
      orderItems.value = { ...items };
    }
    function setOrderList(
      data: {
        orderList: IOrderList;
        pagination: typeof PAGINATION;
      },
      filterShaft?: boolean,
    ) {
      if (isMobile.value) {
        if (typeof filterShaft === 'boolean' && filterShaft) {
          orderList.value = data.orderList;
        } else {
          if (data.orderList.length < 1) {
            orderList.value = data.orderList;
          } else {
            data.orderList.forEach((order: any) => {
              orderList.value.push(order);
            });
          }
        }
      } else {
        orderList.value = data.orderList;
      }
      CallSetPagination(data.pagination);
    }
    function setOrderStatements(value: any) {
      orderStatements.value = value;
    }
    function resetFilters() {
      orderListFilters.value = { ...ORDER_LIST_FILTERS };
    }
    function setExpireTimeout() {
      const expirationDate = new Date();
      expirationDate.setMinutes(expirationDate.getMinutes() + 3);
      expiration.value = expirationDate;
    }
    async function dispatchGetOrders(onFailure?: Fn, onSuccess?: Fn, force = false) {
      loadingOrders.value = true;
      try {
        if (!isExpired.value && !force) return;

        const data = await listOrders(session.value.currentEconomicGroupMember);
        setOrderList(data);
        setExpireTimeout();

        if (onSuccess) onSuccess();

        return;
      } catch (error) {
        setOrderList({ orderList: [], pagination: { ...PAGINATION } });

        if (onFailure) onFailure(error);
      } finally {
        loadingOrders.value = false;
      }
    }

    async function onChangeEconomicGroup() {
      return Promise.all([
        dispatchGetOrders(undefined, undefined, true),
        showLastOrder(session.value.currentEconomicGroupMember),
      ]).then(([orders, lastOrder]) => {
        isNotEmptyValue(lastOrder) && 'orderNumber' in lastOrder
          ? setLastOrder(lastOrder)
          : setLastOrder(null);
      });
    }

    async function onSessionOpen() {
      return Promise.all([
        dispatchGetOrders(),
        showLastOrder(session.value.currentEconomicGroupMember),
      ]).then(([orders, lastOrder]) => {
        isNotEmptyValue(lastOrder) && 'orderNumber' in lastOrder
          ? setLastOrder(lastOrder)
          : setLastOrder(null);
      });
    }

    function onSessionClose() {
      setOrderList({ orderList: [], pagination: { ...PAGINATION } });
      setLastOrder(null);
      setOrderItems({} as IOrderItems);
      setOrder({} as IOrder);
      resetFilters();
    }

    return {
      order,
      lastOrder,
      orderItems,
      orderList,
      orderListFilters,
      orderListPagination,
      orderStatements,
      expiration,
      hasOrderItems,
      hasInvoicedItems,
      hasUnbilledItems,
      orderItemsInvoiced,
      noDataFiltered,
      hasFilteredData,
      isNotOneTimePayment,
      isExpired,
      loadingOrders,
      CallOrderListPaginate,
      setLastOrder,
      setOrder,
      setOrderItems,
      setOrderList,
      setOrderStatements,
      resetFilters,
      setExpireTimeout,
      dispatchGetOrders,
      onSessionOpen,
      onSessionClose,
      onChangeEconomicGroup,
    };
  },
  {
    persist: true,
  },
);
